import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { GoogleMap, Marker, OverlayView, Polygon, useLoadScript } from "@react-google-maps/api";
import { booleanContains } from "@turf/boolean-contains";
import { booleanOverlap } from "@turf/boolean-overlap";
import { booleanWithin } from "@turf/boolean-within";
import { polygon as turfPolygon } from "@turf/helpers";
import { CatButton } from "blocks-nextjs/blocks-react/components/Button/Button";
import { CatIconAdd } from "blocks-nextjs/blocks-react/components/Icons/Add/Add";
import { CatIconCancel } from "blocks-nextjs/blocks-react/components/Icons/Cancel/Cancel";
import { CatIconCancelUndo } from "blocks-nextjs/blocks-react/components/Icons/CancelUndo/CancelUndo";
import { CatIconCloseCircle } from "blocks-nextjs/blocks-react/components/Icons/CloseCircle/CloseCircle";
import { CatIconReorder } from "blocks-nextjs/blocks-react/components/Icons/Reorder/Reorder";
import { CatIconUndo } from "blocks-nextjs/blocks-react/components/Icons/Undo/Undo";
import { CatTextPassage } from "blocks-nextjs/blocks-react/components/TextPassage/TextPassage";
import { CatTooltip } from "blocks-nextjs/blocks-react/components/Tooltip/Tooltip";
import { useCallback, useEffect, useRef, useState } from "react";
import { TerraDraw, TerraDrawPolygonMode } from "terra-draw";
import { TerraDrawGoogleMapsAdapter } from "terra-draw-google-maps-adapter";
import { getGoogleMapsApiKey } from "../../lib/getEnvVariables";
import useGlobalStore from "../../store/useGlobalStore";
import useProjectGeoFenceStore from "../../store/useProjectGeoFenceStore";
import { Variant } from "../GlobalNotification/GlobalNotification";
import { convertGmapPathToGeoJSONPath, getGeofenceTypeName } from "./GeoFence.helper";
import { POLYGON_TYPES } from "./GoogleMap.interface";
// Re-exported for backwards compatibility; canonical source is GoogleMap.interface.ts
export { POLYGON_TYPES };
const defaultMapZoom = 13;
const PROJECT_AREA_Z_INDEX = 1;
const SELECTED_POLYGON_Z_INDEX = 1002;
const BASE_POLYGON_Z_INDEX = 1000;
const MODE_ADD = "add";
const MODE_EDIT = "edit";
const DRAWING_MODE_POLYGON = "polygon";
const MIN_POLYGON_POINTS = 3;
const DRAWING_POINTER_DISTANCE = 20;
const POLYGON_FILL_OPACITY = 0.2;
const POLYGON_STROKE_WEIGHT_DEFAULT = 2;
const COLOR_PROJECT_AREA_FILL = "#8CBAFF";
const COLOR_PROJECT_AREA_STROKE = "#0066FF";
const COLOR_JOB_SITE = "#272DBD";
const COLOR_CHARGE_STATION = "#18AEA5";
const COLOR_DEFAULT_LABEL = "#0084e8";
const COLOR_WHITE_HEX = "#ffffff";
const LEGEND_FILL_ALPHA = "B3";
const LEGEND_SWATCH_SIZE = "12px";
const LEGEND_PADDING = "10px";
const LEGEND_CONTROL_ID = "geofence-color-legend";
var RenderOrder;
(function (RenderOrder) {
    RenderOrder[RenderOrder["RENDER_FIRST"] = -1] = "RENDER_FIRST";
    RenderOrder[RenderOrder["RENDER_LAST"] = 1] = "RENDER_LAST";
})(RenderOrder || (RenderOrder = {}));
var PolygonStrokeWeight;
(function (PolygonStrokeWeight) {
    PolygonStrokeWeight[PolygonStrokeWeight["DEFAULT"] = 2] = "DEFAULT";
    PolygonStrokeWeight[PolygonStrokeWeight["EDIT"] = 3] = "EDIT";
})(PolygonStrokeWeight || (PolygonStrokeWeight = {}));
function GoogleMapGeoFence({ currentSelectedPolygonId, currentSelectedPolygonType, deletePolygonIdInfo, latLangValues = null, projectGeoFenceInfo, resetDeletePolygonId, resetUpdateMapGeoFenceId, setSelectedGeoFenceIdInfo, updateGeoFencePath, updatePolygonId }) {
    const { isLoaded } = useLoadScript({
        googleMapsApiKey: getGoogleMapsApiKey()
    });
    const { setGlobalNotification } = useGlobalStore((state) => ({
        setGlobalNotification: state.setGlobalNotification
    }));
    const { getGeofenceCoordinates, getGeofenceModeType, getGeofenceName, getGeofenceType } = useProjectGeoFenceStore((state) => ({
        getGeofenceCoordinates: state.getGeofenceCoordinates,
        getGeofenceModeType: state.getGeofenceModeType,
        getGeofenceName: state.getGeofenceName,
        getGeofenceType: state.getGeofenceType
    }));
    const [polygons, setPolygons] = useState([]);
    // Initialize polygons from projectGeoFenceInfo
    useEffect(() => {
        if (projectGeoFenceInfo && projectGeoFenceInfo.length > 0) {
            const newPolygons = projectGeoFenceInfo.map((info) => ({
                geofenceId: info.geofenceId,
                geofenceType: info.geofenceType,
                options: getPolygonOptions(info.geofenceType),
                path: info.coordinates
            }));
            setPolygons(newPolygons);
        }
    }, [projectGeoFenceInfo]);
    useEffect(() => {
        if (!currentSelectedPolygonId) {
            setEditPolygonId(null);
            setDrawingMode(null);
            return;
        }
        if (currentSelectedPolygonId) {
            if (getGeofenceModeType(currentSelectedPolygonId) !== MODE_ADD) {
                setDrawingMode(null);
            }
            if (getGeofenceModeType(currentSelectedPolygonId) === MODE_ADD) {
                setEditPolygonId(null);
            }
        }
    }, [currentSelectedPolygonId, getGeofenceModeType]);
    // Track the currently selected polygon for editing
    const [editPolygonId, setEditPolygonId] = useState(null);
    const [hoveredPolygonId, setHoveredPolygonId] = useState(null);
    // Track drawing mode
    const [drawingMode, setDrawingMode] = useState(null);
    const [mapInstance, setMapInstance] = useState(null);
    const [isTerraDrawReady, setIsTerraDrawReady] = useState(false);
    const terraDrawRef = useRef(null);
    const polygonModeRef = useRef(null);
    const handlePolygonCompleteRef = useRef(() => { });
    const onEditRef = useRef(() => { });
    const isValidPolygonRef = useRef(() => true);
    const updateHistoryRef = useRef(() => { });
    const draggingPolygonIdRef = useRef(null);
    const suppressEditClickRef = useRef(false);
    const suppressEditClickTimerRef = useRef(null);
    // Define refs for Polygon instances and listeners
    const polygonRefs = useRef({});
    const listenersRefs = useRef({});
    const polygonInfoRef = useRef({});
    const [currentPolygonType, setCurrentPolygonType] = useState(POLYGON_TYPES.NONE);
    const [mapView, setMapView] = useState({
        coordinates: { ...latLangValues },
        zoom: defaultMapZoom
    });
    useEffect(() => {
        if (deletePolygonIdInfo) {
            if (deletePolygonIdInfo.geofenceType === POLYGON_TYPES.PROJECT_AREA) {
                setPolygons([]);
            }
            if (deletePolygonIdInfo.geofenceType === POLYGON_TYPES.JOB_SITE ||
                deletePolygonIdInfo.geofenceType === POLYGON_TYPES.CHARGE_STATION) {
                setPolygons((prevPolygons) => prevPolygons.filter((poly) => poly.geofenceId !== deletePolygonIdInfo.geofenceId));
            }
            setDrawingMode(null);
            setEditPolygonId(null);
            resetDeletePolygonId();
        }
    }, [deletePolygonIdInfo]);
    // Keep polygonInfoRef in sync with polygons state for onEdit callback
    useEffect(() => {
        polygonInfoRef.current = polygons.reduce((accumulator, poly) => {
            accumulator[poly.geofenceId] = poly;
            return accumulator;
        }, {});
    }, [polygons]);
    useEffect(() => {
        if (updatePolygonId) {
            const { newGeofenceId, oldGeoefnceId } = updatePolygonId;
            setPolygons((prevPolygons) => prevPolygons.map((poly) => {
                if (poly.geofenceId === oldGeoefnceId) {
                    return { ...poly, geofenceId: newGeofenceId };
                }
                return poly;
            }));
            resetUpdateMapGeoFenceId();
        }
    }, [updatePolygonId]);
    const isUpdateNewPolygonType = () => {
        const hasDrawnPolygon = polygons.some((poly) => poly.geofenceId === currentSelectedPolygonId);
        const hasCoordinates = (getGeofenceCoordinates(currentSelectedPolygonId)?.length ?? 0) > 0;
        return hasDrawnPolygon && hasCoordinates;
    };
    useEffect(() => {
        if (currentSelectedPolygonType !== POLYGON_TYPES.NONE) {
            setCurrentPolygonType(currentSelectedPolygonType);
            if (getGeofenceModeType(currentSelectedPolygonId) === MODE_ADD) {
                //check is new polygon or not
                if (isAddPolygonButtonEnabled()) {
                    setEditPolygonId(null);
                    setDrawingMode(DRAWING_MODE_POLYGON);
                }
                if (isUpdateNewPolygonType()) {
                    //update polygonType
                    setPolygons((prevPolygons) => prevPolygons.map((poly) => {
                        if (poly.geofenceId === currentSelectedPolygonId) {
                            return {
                                ...poly,
                                geofenceType: currentSelectedPolygonType,
                                options: getPolygonOptions(currentSelectedPolygonType)
                            };
                        }
                        return poly;
                    }));
                }
            }
            if (getGeofenceModeType(currentSelectedPolygonId) === MODE_EDIT) {
                setEditPolygonId(null);
                setDrawingMode(null);
            }
        }
    }, [currentSelectedPolygonType]);
    const getCapitalized = (str) => {
        return str.charAt(0).toUpperCase() + str.slice(1);
    };
    const isValidPolygon = (newPolygon) => {
        if (polygons.length === 0) {
            return true;
        }
        const geoJSONPathNewPolygonArray = convertGmapPathToGeoJSONPath([newPolygon]);
        const newPolygonInfo = geoJSONPathNewPolygonArray[0];
        const geoJSONPathPolygonsInfo = convertGmapPathToGeoJSONPath(polygons.filter((p) => p.geofenceId !== newPolygon.geofenceId));
        const projectAreaPolygons = geoJSONPathPolygonsInfo.filter((polygon) => polygon.geofenceType === POLYGON_TYPES.PROJECT_AREA);
        const chargeStationWorkSitePolygons = geoJSONPathPolygonsInfo.filter((polygon) => polygon.geofenceType !== POLYGON_TYPES.PROJECT_AREA);
        const newTurfPolygon = turfPolygon([newPolygonInfo.path]);
        // validate workstation and site area are inside project area if new polygon is project area
        if (chargeStationWorkSitePolygons.length > 0 && newPolygonInfo.geofenceType === POLYGON_TYPES.PROJECT_AREA) {
            for (const polygon of chargeStationWorkSitePolygons) {
                const turfPolygonInfo = turfPolygon([polygon.path]);
                if (!booleanContains(newTurfPolygon, turfPolygonInfo)) {
                    setGlobalNotification({
                        message: `${getGeofenceName(polygon.geofenceId)} must be fully inside ${getGeofenceName(newPolygonInfo.geofenceId)}`,
                        showNotification: true,
                        type: Variant.warning
                    });
                    return false;
                }
            }
        }
        // Validate new polygon against existing polygons
        if (newPolygonInfo.geofenceType !== POLYGON_TYPES.PROJECT_AREA &&
            (chargeStationWorkSitePolygons.length > 0 || projectAreaPolygons.length > 0)) {
            //validate new polygon is inside project area polygon
            if (projectAreaPolygons.length === 1) {
                const projectAreaPolygon = projectAreaPolygons[0];
                const turfPolygonInfo = turfPolygon([projectAreaPolygon.path]);
                if (!booleanContains(turfPolygonInfo, newTurfPolygon)) {
                    setGlobalNotification({
                        message: `${getGeofenceTypeName(newPolygonInfo.geofenceType)} ${getGeofenceName(newPolygonInfo.geofenceId)} must be fully inside ${getGeofenceName(projectAreaPolygon.geofenceId)}`,
                        showNotification: true,
                        type: Variant.warning
                    });
                    return false;
                }
            }
            // Check if the new polygon overlaps with any existing charge station or work site polygons
            for (const polygon of chargeStationWorkSitePolygons) {
                const turfPolygonInfo = turfPolygon([polygon.path]);
                const hasOverlap = booleanOverlap(turfPolygonInfo, newTurfPolygon);
                const hasContainment = booleanContains(turfPolygonInfo, newTurfPolygon) ||
                    booleanContains(newTurfPolygon, turfPolygonInfo) ||
                    booleanWithin(turfPolygonInfo, newTurfPolygon) ||
                    booleanWithin(newTurfPolygon, turfPolygonInfo);
                if (hasOverlap || hasContainment) {
                    setGlobalNotification({
                        message: `${getCapitalized(getGeofenceName(newPolygonInfo.geofenceId))} must not overlap with ${getCapitalized(getGeofenceName(polygon.geofenceId))}`,
                        showNotification: true,
                        type: Variant.warning
                    });
                    return false;
                }
            }
        }
        return true;
    };
    useEffect(() => {
        isValidPolygonRef.current = isValidPolygon;
    }, [isValidPolygon]);
    const renderLegend = (map) => {
        const controls = map.controls[google.maps.ControlPosition.LEFT_BOTTOM];
        for (let i = controls.getLength() - 1; i >= 0; i -= 1) {
            const controlNode = controls.getAt(i);
            if (controlNode?.id === LEGEND_CONTROL_ID) {
                controls.removeAt(i);
            }
        }
        const legend = document.createElement("div");
        legend.id = LEGEND_CONTROL_ID;
        legend.style.backgroundColor = "white";
        legend.style.border = "2px solid #fff";
        legend.style.borderRadius = "10px";
        legend.style.boxShadow = "0 2px 6px rgba(0,0,0,.3)";
        legend.style.margin = "10px";
        legend.style.padding = "10px";
        legend.innerHTML = "<h4 style='color:grey'>Color codes</h4>";
        const constructionAreaLegend = document.createElement("div");
        constructionAreaLegend.className =
            "flex items-center gap-2 size pt-[10px] pb-[10px] text-[12px] font-medium opacity-[0.7]";
        constructionAreaLegend.innerHTML = `<div style="border: 1px solid ${getPolygonOptions(POLYGON_TYPES.PROJECT_AREA).fillColor}; width: ${LEGEND_SWATCH_SIZE}; height: ${LEGEND_SWATCH_SIZE}; display: inline-block; background: ${getPolygonOptions(POLYGON_TYPES.PROJECT_AREA).fillColor}${LEGEND_FILL_ALPHA};"></div> Project Area`;
        legend.appendChild(constructionAreaLegend);
        const jobSiteLegend = document.createElement("div");
        jobSiteLegend.className = `flex items-center gap-2 pb-[${LEGEND_PADDING}] text-[12px] font-medium opacity-[0.7]`;
        jobSiteLegend.innerHTML = `<div style="border: 1px solid ${getPolygonOptions(POLYGON_TYPES.JOB_SITE).fillColor}; width: ${LEGEND_SWATCH_SIZE}; height: ${LEGEND_SWATCH_SIZE}; display: inline-block; background: ${getPolygonOptions(POLYGON_TYPES.JOB_SITE).fillColor}${LEGEND_FILL_ALPHA};"></div> Job Site`;
        legend.appendChild(jobSiteLegend);
        const chargeStationLegend = document.createElement("div");
        chargeStationLegend.className = `flex items-center gap-2 pb-[${LEGEND_PADDING}] text-[12px] font-medium opacity-[0.7]`;
        chargeStationLegend.innerHTML = `<div style="border: 1px solid ${getPolygonOptions(POLYGON_TYPES.CHARGE_STATION).fillColor}; width: ${LEGEND_SWATCH_SIZE}; height: ${LEGEND_SWATCH_SIZE}; display: inline-block; background: ${getPolygonOptions(POLYGON_TYPES.CHARGE_STATION).fillColor}${LEGEND_FILL_ALPHA};"></div> Charge Station`;
        legend.appendChild(chargeStationLegend);
        controls.push(legend);
    };
    const getPolygonOptions = (geofenceType) => {
        const strokeWeight = POLYGON_STROKE_WEIGHT_DEFAULT;
        const fillOpacity = POLYGON_FILL_OPACITY;
        switch (geofenceType) {
            case POLYGON_TYPES.PROJECT_AREA:
                return {
                    fillColor: COLOR_PROJECT_AREA_FILL,
                    fillOpacity,
                    strokeColor: COLOR_PROJECT_AREA_STROKE,
                    strokeWeight
                };
            case POLYGON_TYPES.JOB_SITE:
                return {
                    fillColor: COLOR_JOB_SITE,
                    fillOpacity,
                    strokeColor: COLOR_JOB_SITE,
                    strokeWeight
                };
            case POLYGON_TYPES.CHARGE_STATION:
                return {
                    fillColor: COLOR_CHARGE_STATION,
                    fillOpacity,
                    strokeColor: COLOR_CHARGE_STATION,
                    strokeWeight
                };
            default:
                return {};
        }
    };
    const getDrawModeOptions = (geofenceType) => {
        const polygonOptions = getPolygonOptions(geofenceType);
        const drawColor = (polygonOptions.strokeColor ?? COLOR_DEFAULT_LABEL);
        return {
            keyEvents: {
                cancel: "Escape",
                finish: "Enter"
            },
            pointerDistance: DRAWING_POINTER_DISTANCE,
            showCoordinatePoints: true,
            styles: {
                closingPointColor: drawColor,
                closingPointOpacity: 1,
                closingPointOutlineColor: COLOR_WHITE_HEX,
                closingPointOutlineOpacity: 1,
                closingPointOutlineWidth: 2,
                closingPointWidth: 10,
                coordinatePointColor: drawColor,
                coordinatePointOpacity: 1,
                coordinatePointOutlineColor: COLOR_WHITE_HEX,
                coordinatePointOutlineOpacity: 1,
                coordinatePointOutlineWidth: 1,
                coordinatePointWidth: 6,
                fillColor: drawColor,
                fillOpacity: POLYGON_FILL_OPACITY,
                outlineColor: drawColor,
                outlineOpacity: 1,
                outlineWidth: POLYGON_STROKE_WEIGHT_DEFAULT
            }
        };
    };
    const handlePolygonComplete = useCallback((path) => {
        if (!currentSelectedPolygonId) {
            setDrawingMode(null);
            return;
        }
        if (path?.length < MIN_POLYGON_POINTS) {
            setGlobalNotification({
                message: "Polygon must have at least 3 points.",
                showNotification: true,
                type: Variant.warning
            });
            return;
        }
        const newPolygon = {
            geofenceId: currentSelectedPolygonId,
            geofenceType: currentPolygonType,
            options: getPolygonOptions(currentPolygonType),
            path: path
        };
        suppressEditClickRef.current = true;
        if (suppressEditClickTimerRef.current) {
            window.clearTimeout(suppressEditClickTimerRef.current);
        }
        suppressEditClickTimerRef.current = window.setTimeout(() => {
            suppressEditClickRef.current = false;
        }, 250);
        // Use ref to quickly short-circuit first polygon; full validation still uses current state via dependencies.
        const existingPolygons = Object.keys(polygonInfoRef.current);
        const isValid = existingPolygons.length === 0 ? true : isValidPolygon(newPolygon);
        setPolygons((prevPolygons) => [...prevPolygons, newPolygon]);
        setEditPolygonId(null);
        if (isValid) {
            setDrawingMode(null);
            updateGeoFencePath({
                coordinates: newPolygon.path,
                geofenceId: newPolygon.geofenceId
            });
        }
        else {
            setDrawingMode(null);
            updateGeoFencePath({
                coordinates: [],
                geofenceId: newPolygon.geofenceId
            });
        }
    }, [currentSelectedPolygonId, currentPolygonType, polygons, setGlobalNotification, updateGeoFencePath]);
    useEffect(() => {
        handlePolygonCompleteRef.current = handlePolygonComplete;
    }, [handlePolygonComplete]);
    useEffect(() => {
        if (!mapInstance || terraDrawRef.current) {
            return;
        }
        if (typeof mapInstance.addListener !== "function") {
            return;
        }
        const initializeTerraDraw = () => {
            if (terraDrawRef.current || typeof mapInstance.getProjection !== "function") {
                return;
            }
            if (!mapInstance.getProjection()) {
                return;
            }
            const polygonMode = new TerraDrawPolygonMode(getDrawModeOptions(currentPolygonType));
            const draw = new TerraDraw({
                adapter: new TerraDrawGoogleMapsAdapter({
                    coordinatePrecision: 9,
                    lib: google.maps,
                    map: mapInstance
                }),
                modes: [polygonMode]
            });
            setIsTerraDrawReady(false);
            // Register 'ready' listener BEFORE calling start() to prevent missing the event
            draw.on("ready", () => {
                setIsTerraDrawReady(true);
            });
            polygonModeRef.current = polygonMode;
            terraDrawRef.current = draw;
            draw.start();
        };
        initializeTerraDraw();
        const projectionListener = mapInstance.addListener("projection_changed", initializeTerraDraw);
        return () => {
            projectionListener.remove();
        };
    }, [mapInstance, currentPolygonType]);
    useEffect(() => {
        if (!polygonModeRef.current) {
            return;
        }
        polygonModeRef.current.updateOptions(getDrawModeOptions(currentPolygonType));
    }, [currentPolygonType]);
    useEffect(() => {
        const draw = terraDrawRef.current;
        if (!draw || !isTerraDrawReady) {
            return;
        }
        if (drawingMode === DRAWING_MODE_POLYGON) {
            draw.setMode(DRAWING_MODE_POLYGON);
            return;
        }
        draw.setMode("static");
        draw.clear();
    }, [drawingMode, isTerraDrawReady]);
    useEffect(() => {
        const draw = terraDrawRef.current;
        if (!draw || !isTerraDrawReady) {
            return;
        }
        const onFinish = (featureId, context) => {
            if (context.mode !== DRAWING_MODE_POLYGON || context.action !== "draw") {
                return;
            }
            const completedFeature = draw
                .getSnapshot()
                .find((feature) => String(feature.id) === String(featureId) && feature.geometry.type === "Polygon");
            if (!completedFeature || completedFeature.geometry.type !== "Polygon") {
                return;
            }
            const ringCoordinates = completedFeature.geometry.coordinates[0] ?? [];
            const pathCoordinates = ringCoordinates
                .slice(0, ringCoordinates.length > 1 &&
                ringCoordinates[0][0] === ringCoordinates[ringCoordinates.length - 1][0] &&
                ringCoordinates[0][1] === ringCoordinates[ringCoordinates.length - 1][1]
                ? -1
                : ringCoordinates.length)
                .map(([lng, lat]) => ({ lat, lng }));
            handlePolygonCompleteRef.current(pathCoordinates);
            draw.removeFeatures([String(featureId)]);
        };
        draw.on("finish", onFinish);
        return () => {
            draw.off("finish", onFinish);
        };
    }, [isTerraDrawReady]);
    useEffect(() => {
        return () => {
            if (suppressEditClickTimerRef.current) {
                window.clearTimeout(suppressEditClickTimerRef.current);
                suppressEditClickTimerRef.current = null;
            }
            if (terraDrawRef.current) {
                terraDrawRef.current.stop();
                terraDrawRef.current = null;
            }
            polygonModeRef.current = null;
        };
    }, []);
    const onEdit = useCallback((geofenceId, forceCommit = false) => {
        if (!forceCommit && draggingPolygonIdRef.current === geofenceId) {
            return;
        }
        if (polygonRefs.current[geofenceId]) {
            const polygon = polygonRefs.current[geofenceId];
            const nextPath = polygon
                .getPath()
                .getArray()
                .map((latLng) => {
                return { lat: latLng.lat(), lng: latLng.lng() };
            });
            // Get current polygon info from ref instead of state to avoid stale closures
            const editPolygonInfo = polygonInfoRef.current[geofenceId];
            // Guard against undefined editPolygonInfo (may not be synced yet during edit events)
            if (!editPolygonInfo) {
                return;
            }
            if (JSON.stringify(nextPath) === JSON.stringify(editPolygonInfo.path)) {
                return;
            }
            const isValid = isValidPolygonRef.current({ ...editPolygonInfo, path: nextPath });
            if (isValid) {
                updateHistoryRef.current(geofenceId, nextPath);
            }
            setPolygons((prevPolygons) => prevPolygons.map((poly) => {
                if (poly.geofenceId === geofenceId) {
                    updateGeoFencePath({
                        coordinates: isValid ? nextPath : [],
                        geofenceId: geofenceId
                    });
                    return { ...poly, path: nextPath };
                }
                else {
                    return poly;
                }
            }));
        }
    }, [updateGeoFencePath]);
    useEffect(() => {
        onEditRef.current = onEdit;
    }, [onEdit]);
    const onPolygonLoad = useCallback((polygon, geofenceId) => {
        polygonRefs.current[geofenceId] = polygon;
        const path = polygon.getPath();
        if (!listenersRefs.current[geofenceId]) {
            listenersRefs.current[geofenceId] = [];
        }
        listenersRefs.current[geofenceId].push(path.addListener("set_at", () => onEditRef.current(geofenceId)), path.addListener("insert_at", () => onEditRef.current(geofenceId)), path.addListener("remove_at", () => onEditRef.current(geofenceId)));
    }, []);
    const onPolygonUnmount = useCallback((geofenceId) => {
        if (listenersRefs.current[geofenceId]) {
            listenersRefs.current[geofenceId].forEach((listener) => listener.remove());
            delete listenersRefs.current[geofenceId];
        }
        if (polygonRefs.current[geofenceId]) {
            delete polygonRefs.current[geofenceId];
        }
    }, []);
    const [history, setHistory] = useState({});
    const [historyIndex, setHistoryIndex] = useState({});
    // Initialize history when polygons are loaded
    useEffect(() => {
        if (polygons.length > 0) {
            const initialHistory = {};
            const initialHistoryIndex = {};
            polygons.forEach((poly) => {
                // Only initialize if not already present to avoid overwriting edit history
                if (!history[poly.geofenceId]) {
                    initialHistory[poly.geofenceId] = [poly.path];
                    initialHistoryIndex[poly.geofenceId] = 0;
                }
            });
            if (Object.keys(initialHistory).length > 0) {
                setHistory((prev) => ({ ...prev, ...initialHistory }));
                setHistoryIndex((prev) => ({ ...prev, ...initialHistoryIndex }));
            }
        }
    }, [polygons, history]);
    const updateHistory = useCallback((geofenceId, newPath) => {
        setHistoryIndex((prevIndex) => {
            const currentIndex = prevIndex[geofenceId] ?? -1;
            const nextIndex = currentIndex + 1;
            setHistory((prevHistory) => {
                const currentHistory = prevHistory[geofenceId] || [];
                const newHistory = [...currentHistory.slice(0, currentIndex + 1), newPath];
                return { ...prevHistory, [geofenceId]: newHistory };
            });
            return { ...prevIndex, [geofenceId]: nextIndex };
        });
    }, []);
    useEffect(() => {
        updateHistoryRef.current = updateHistory;
    }, [updateHistory]);
    const handleUndo = () => {
        if (!editPolygonId)
            return;
        const currentIndex = historyIndex[editPolygonId];
        if (currentIndex > 0) {
            const newIndex = currentIndex - 1;
            const path = history[editPolygonId][newIndex];
            setHistoryIndex((prev) => ({ ...prev, [editPolygonId]: newIndex }));
            setPolygons((prevPolygons) => prevPolygons.map((poly) => (poly.geofenceId === editPolygonId ? { ...poly, path: path } : poly)));
            updateGeoFencePath({ coordinates: path, geofenceId: editPolygonId });
        }
    };
    const handleRedo = () => {
        if (!editPolygonId)
            return;
        const currentIndex = historyIndex[editPolygonId];
        const currentHistory = history[editPolygonId];
        if (currentIndex < currentHistory.length - 1) {
            const newIndex = currentIndex + 1;
            const path = history[editPolygonId][newIndex];
            setHistoryIndex((prev) => ({ ...prev, [editPolygonId]: newIndex }));
            setPolygons((prevPolygons) => prevPolygons.map((poly) => (poly.geofenceId === editPolygonId ? { ...poly, path: path } : poly)));
            updateGeoFencePath({ coordinates: path, geofenceId: editPolygonId });
        }
    };
    const handleClearAll = () => {
        if (!editPolygonId)
            return;
        const initialPath = history[editPolygonId]?.[0];
        if (!initialPath)
            return;
        // Revert polygon to its initial state
        setPolygons((prevPolygons) => prevPolygons.map((poly) => (poly.geofenceId === editPolygonId ? { ...poly, path: initialPath } : poly)));
        setHistory((prev) => ({
            ...prev,
            [editPolygonId]: [initialPath]
        }));
        setHistoryIndex((prev) => ({
            ...prev,
            [editPolygonId]: 0
        }));
        updateGeoFencePath({ coordinates: initialPath, geofenceId: editPolygonId });
    };
    const isAddPolygonButtonEnabled = () => {
        if (!currentSelectedPolygonId) {
            return false;
        }
        const mode = getGeofenceModeType(currentSelectedPolygonId);
        if (mode !== MODE_ADD && mode !== MODE_EDIT) {
            return false;
        }
        const hasDrawnPolygon = polygons.some((poly) => poly.geofenceId === currentSelectedPolygonId);
        const hasCoordinates = (getGeofenceCoordinates(currentSelectedPolygonId)?.length ?? 0) > 0;
        return !hasDrawnPolygon && !hasCoordinates;
    };
    const mapOptions = {
        cameraControl: false,
        streetViewControl: false
    };
    return isLoaded ? (_jsx("div", { className: "h-full", children: _jsxs(GoogleMap, { center: mapView?.coordinates, mapContainerStyle: { height: "100%", width: "100%" }, onClick: () => {
                if (!drawingMode) {
                    setEditPolygonId(null);
                }
            }, onLoad: (map) => {
                setMapInstance(map);
                renderLegend(map);
                map.addListener("idle", () => {
                    const center = map.getCenter();
                    if (center) {
                        const lat = center.lat();
                        const lng = center.lng();
                        const zoom = map.getZoom() ?? 0;
                        setMapView((prev) => {
                            if (prev &&
                                prev.coordinates.lat === lat &&
                                prev.coordinates.lng === lng &&
                                prev.zoom === zoom) {
                                return prev;
                            }
                            return { coordinates: { lat, lng }, zoom };
                        });
                    }
                });
            }, options: mapOptions, zoom: mapView?.zoom, children: [_jsx(Marker, { position: latLangValues }), polygons.map((polygon) => {
                    const geofenceName = getGeofenceName(polygon.geofenceId);
                    const shouldHidePill = !geofenceName || geofenceName.trim() === "";
                    if (polygon.geofenceId !== hoveredPolygonId || shouldHidePill) {
                        return null;
                    }
                    const center = polygon.path.reduce((acc, point) => {
                        acc.lat += point.lat;
                        acc.lng += point.lng;
                        return acc;
                    }, { lat: 0, lng: 0 });
                    center.lat /= polygon.path.length;
                    center.lng /= polygon.path.length;
                    return (_jsx(OverlayView, { mapPaneName: OverlayView.OVERLAY_MOUSE_TARGET, position: center, children: _jsxs("div", { className: "inline-flex items-center gap-2 rounded-full px-2 text-white shadow cursor-pointer select-none font-noto font-[400] text-[17px] leading-[13px] tracking-[0] whitespace-nowrap", onClick: () => {
                                if (drawingMode === DRAWING_MODE_POLYGON) {
                                    return;
                                }
                                if (suppressEditClickRef.current) {
                                    return;
                                }
                                setEditPolygonId(polygon.geofenceId);
                                setSelectedGeoFenceIdInfo(polygon.geofenceId);
                            }, style: {
                                backgroundColor: polygon.geofenceType === POLYGON_TYPES.PROJECT_AREA
                                    ? COLOR_PROJECT_AREA_FILL
                                    : polygon.geofenceType === POLYGON_TYPES.JOB_SITE
                                        ? COLOR_JOB_SITE
                                        : polygon.geofenceType === POLYGON_TYPES.CHARGE_STATION
                                            ? COLOR_CHARGE_STATION
                                            : COLOR_DEFAULT_LABEL
                            }, children: [_jsx("svg", { "data-test": `polygon-marker-${polygon.geofenceId}`, height: "34", width: "24", children: _jsx("circle", { cx: "12", cy: "17", fill: "none", r: "9", stroke: "#fff", strokeDasharray: "4,4", strokeWidth: "1.5" }) }), geofenceName] }) }, polygon.geofenceId));
                }), [...polygons]
                    .sort((a, b) => {
                    // Rule 1: projectArea polygon should be at the bottom (rendered first)
                    if (a.geofenceType === POLYGON_TYPES.PROJECT_AREA)
                        return RenderOrder.RENDER_FIRST;
                    if (b.geofenceType === POLYGON_TYPES.PROJECT_AREA)
                        return RenderOrder.RENDER_LAST;
                    // Rule 2: Keep selected polygon on top of others for editing
                    if (a.geofenceId === editPolygonId)
                        return RenderOrder.RENDER_LAST;
                    if (b.geofenceId === editPolygonId)
                        return RenderOrder.RENDER_FIRST;
                    return 0;
                })
                    .map((polygon, index) => (_jsx(Polygon, { "data-test": `polygon-${polygon.geofenceId}`, onClick: (e) => {
                        e.domEvent.stopPropagation();
                        if (drawingMode === DRAWING_MODE_POLYGON) {
                            return;
                        }
                        if (suppressEditClickRef.current) {
                            return;
                        }
                        setEditPolygonId(polygon.geofenceId);
                        setSelectedGeoFenceIdInfo(polygon.geofenceId);
                    }, onDragEnd: () => {
                        onEdit(polygon.geofenceId, true);
                        draggingPolygonIdRef.current = null;
                    }, onDragStart: () => {
                        draggingPolygonIdRef.current = polygon.geofenceId;
                    }, onLoad: (poly) => onPolygonLoad(poly, polygon.geofenceId), onMouseOut: () => setHoveredPolygonId(null), onMouseOver: () => setHoveredPolygonId(polygon.geofenceId), onMouseUp: () => onEdit(polygon.geofenceId, true), onUnmount: () => onPolygonUnmount(polygon.geofenceId), options: {
                        ...polygon.options,
                        clickable: drawingMode !== DRAWING_MODE_POLYGON,
                        draggable: drawingMode !== DRAWING_MODE_POLYGON && editPolygonId === polygon.geofenceId,
                        editable: drawingMode !== DRAWING_MODE_POLYGON && editPolygonId === polygon.geofenceId,
                        strokeWeight: editPolygonId === polygon.geofenceId
                            ? PolygonStrokeWeight.EDIT
                            : PolygonStrokeWeight.DEFAULT,
                        zIndex: polygon.geofenceType === POLYGON_TYPES.PROJECT_AREA
                            ? PROJECT_AREA_Z_INDEX
                            : editPolygonId === polygon.geofenceId
                                ? SELECTED_POLYGON_Z_INDEX
                                : BASE_POLYGON_Z_INDEX + index
                    }, paths: polygon.path }, polygon.geofenceId))), _jsxs("div", { className: "bg-white absolute top-[120px] right-[10px] rounded flex flex-col items-center w-[40px]", children: [drawingMode && (_jsx(_Fragment, { children: _jsxs(CatTooltip, { allowedPlacements: ["left"], "data-test": "cat-tooltip-cancel-drawing", children: [_jsxs(CatButton, { hideText: true, onClick: () => {
                                            setDrawingMode(null);
                                        }, size: "md", slot: "trigger", variant: "ghost", children: ["Cancel Drawing", _jsx(CatIconCancel, { slot: "after" })] }), _jsx(CatTextPassage, { size: "sm", children: "Cancel Drawing" })] }) })), isAddPolygonButtonEnabled() && !drawingMode && (_jsxs(_Fragment, { children: [_jsx("hr", { className: "w-full border-gray-200" }), _jsxs(CatTooltip, { allowedPlacements: ["left"], "data-test": "cat-tooltip-add-polygon", children: [_jsxs(CatButton, { hideText: true, onClick: () => {
                                                if (getGeofenceType(currentSelectedPolygonId) === POLYGON_TYPES.NONE ||
                                                    !getGeofenceType(currentSelectedPolygonId)) {
                                                    setGlobalNotification({
                                                        message: "Please select a geofence type to draw.",
                                                        showNotification: true,
                                                        type: Variant.warning
                                                    });
                                                    return;
                                                }
                                                else {
                                                    setEditPolygonId(null);
                                                    setCurrentPolygonType(getGeofenceType(currentSelectedPolygonId));
                                                    setDrawingMode(DRAWING_MODE_POLYGON);
                                                }
                                            }, size: "md", slot: "trigger", variant: getGeofenceType(currentSelectedPolygonId) === POLYGON_TYPES.NONE ||
                                                !getGeofenceType(currentSelectedPolygonId)
                                                ? "ghost"
                                                : "primary", children: ["Add Polygon", _jsx(CatIconAdd, { slot: "after" })] }), getGeofenceType(currentSelectedPolygonId) === POLYGON_TYPES.NONE ||
                                            !getGeofenceType(currentSelectedPolygonId) ? (_jsx(CatTextPassage, { size: "sm", children: "Select Geofence type to draw polygon" })) : (_jsx(CatTextPassage, { size: "sm", children: "Add Polygon" }))] })] })), editPolygonId && !drawingMode && (_jsxs(_Fragment, { children: [_jsxs(CatTooltip, { allowedPlacements: ["left"], "data-testid": "cat-tooltip-undo", children: [_jsxs(CatButton, { disabled: !editPolygonId || historyIndex[editPolygonId] <= 0, hideText: true, onClick: handleUndo, size: "md", slot: "trigger", variant: "ghost", children: ["Undo", _jsx(CatIconUndo, { slot: "after" })] }), _jsx(CatTextPassage, { size: "sm", children: "Undo" })] }), _jsx("hr", { className: "w-full border-gray-200" }), _jsxs(CatTooltip, { allowedPlacements: ["left"], "data-testid": "cat-tooltip-redo", children: [_jsxs(CatButton, { disabled: !editPolygonId ||
                                                historyIndex[editPolygonId] >= (history[editPolygonId]?.length ?? 0) - 1, hideText: true, onClick: handleRedo, size: "md", slot: "trigger", variant: "ghost", children: ["Redo", _jsx(CatIconCancelUndo, { slot: "after" })] }), _jsx(CatTextPassage, { size: "sm", children: "Redo" })] }), _jsx("hr", { className: "w-full border-gray-200" }), _jsxs(CatTooltip, { allowedPlacements: ["left"], "data-testid": "cat-tooltip-cancel-all", children: [_jsxs(CatButton, { disabled: !editPolygonId || historyIndex[editPolygonId] <= 0, hideText: true, onClick: () => {
                                                if (historyIndex[editPolygonId] > 0) {
                                                    handleClearAll();
                                                }
                                            }, size: "md", slot: "trigger", variant: "ghost", children: ["Cancel All", _jsx(CatIconCancel, { slot: "after" })] }), _jsx(CatTextPassage, { size: "sm", children: "Cancel All" })] }), _jsx("hr", { className: "w-full border-gray-200" }), _jsxs(CatTooltip, { allowedPlacements: ["left"], "data-test": "cat-tooltip-redraw-polygon", children: [_jsxs(CatButton, { disabled: !editPolygonId, hideText: true, onClick: () => {
                                                if (editPolygonId) {
                                                    updateGeoFencePath({
                                                        coordinates: [],
                                                        geofenceId: editPolygonId
                                                    });
                                                    //delete polygon to redraw new polygon
                                                    setPolygons((prev) => prev.filter((p) => p.geofenceId !== editPolygonId));
                                                    setEditPolygonId(null);
                                                    //To draw new polygon
                                                    setCurrentPolygonType(getGeofenceType(currentSelectedPolygonId));
                                                    setDrawingMode(DRAWING_MODE_POLYGON);
                                                }
                                            }, size: "md", slot: "trigger", variant: "ghost", children: ["Redraw Polygon", _jsx(CatIconReorder, { slot: "after" })] }), _jsx(CatTextPassage, { size: "sm", children: "Redraw Polygon" })] }), _jsx("hr", { className: "w-full border-gray-200" }), _jsxs(CatTooltip, { allowedPlacements: ["left"], "data-test": "cat-tooltip-close", children: [_jsxs(CatButton, { "data-test": "editing-options-close-button", disabled: !editPolygonId, hideText: true, onClick: () => {
                                                setEditPolygonId(null);
                                            }, size: "md", slot: "trigger", variant: "ghost", children: ["Close", _jsx(CatIconCloseCircle, { slot: "after" })] }), _jsx(CatTextPassage, { size: "sm", children: "Close" })] })] }))] })] }) })) : null;
}
export default GoogleMapGeoFence;
